import { Trans, useTranslation } from 'react-i18next';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import { SectionTitle, TextV2 } from '@insights-ltd/design-library/components';
import { PractitionerData } from 'api';
import {
  getRoleDescription,
  isAdvancedUser,
  isGroupManager,
  isSuperAdmin,
} from 'utils/role';
import { Link as RouterLink } from 'react-router';
import { Box, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import AccountOrganisationCard from 'components/OrganisationInfoCard/AccountOrganisationCard/AccountOrganisationCard';
import { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import { Role } from 'types/types';
import { useAuth } from 'contexts/AuthContext';
import { formatList } from 'utils/formatList';

const StyledDiv = styled('div')({
  '& > div': {
    marginBottom: '0.5rem !important',
  },
});

const StyledSpan = styled(TextV2)(({ theme }) => ({
  fontWeight: `${theme.typography.fontWeightBold} !important`,
}));

const StyledLink = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  display: 'inline-block',
}));

const StyledOrganisationIcon = styled(OrganisationIcon)(({ theme }) => ({
  fill: theme.palette.green.main,
  height: '20px',
}));

const DetailsDescription = ({
  role,
  id,
  organisationId,
  organisationCount,
  groupCount,
  inviteeHasAccessToShOnly,
}: {
  role: Role;
  id: string;
  organisationId: string;
  organisationCount: number;
  groupCount: number;
  inviteeHasAccessToShOnly: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const isAdmin = isSuperAdmin(role);

  const getOrganisationsText = (): string => {
    if (isSuperAdmin(user?.role) && isSuperAdmin(role)) {
      return t(
        'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.organisations.sa',
      );
    }
    const stringArr: string[] = [];

    if (isSuperAdmin(user?.role) || inviteeHasAccessToShOnly) {
      stringArr.push(t('ui.event-management.special-handling.user.info'));
    } else if (!isAdvancedUser(role)) {
      if (organisationCount > 0) {
        stringArr.push(
          t(
            'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.organisations',
            { count: organisationCount },
          ),
        );
      }
      if (groupCount > 0) {
        stringArr.push(
          t(
            'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.groups',
            { count: groupCount },
          ),
        );
      }
    } else if (organisationCount > 0) {
      stringArr.push(
        t(
          'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.organisations',
          { count: organisationCount },
        ),
      );
    }

    return formatList(stringArr, i18n.language);
  };

  const organisationsText = getOrganisationsText();

  const userDetailUrlKey = isGroupManager(role)
    ? 'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.link.gm'
    : 'ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.link';
  const userDetailUrl =
    role === 'GROUP_MANAGER'
      ? `/organisations/${organisationId}/practitioners/${id}/edit`
      : `/organisations/${organisationId}/practitioners/${id}`;

  const roleLabel = t(getRoleDescription(role));

  return (
    <Grid
      container
      sx={[
        {
          flexDirection: 'column',
        },
        (theme) => ({
          marginBottom: '1.5rem',
          color: theme.palette.text.secondary,
        }),
      ]}
    >
      <Grid sx={{ lineHeight: '1.75rem' }}>
        <Trans
          i18nKey="ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.user-role"
          values={{ role: roleLabel }}
          components={{
            styledSpan: <StyledSpan component="span" color="textSecondary" />,
          }}
        />
      </Grid>
      <Grid sx={{ lineHeight: '1.75rem' }}>
        <Trans
          i18nKey="ui.event-management.users.add-existing-practitioner.current-organisations.descriptions.has-access-to"
          values={{ organisationsText }}
          components={{
            styledSpan: <StyledSpan component="span" color="textSecondary" />,
          }}
        />
      </Grid>
      <Box
        sx={{
          mt: '1.75rem',
        }}
      />
      <Grid>
        {!isAdmin ? (
          <Trans
            i18nKey={userDetailUrlKey}
            components={{
              styledLink: (
                <StyledLink
                  to={userDetailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        ) : null}
      </Grid>
    </Grid>
  );
};

const ExistingUserDetails = ({
  user,
  organisations,
  orgsInGroups,
  groupCount = 0,
  organisationCount = 0,
}: {
  user?: PractitionerData;
  organisations: any[];
  orgsInGroups: GroupOrganisationHashMap;
  groupCount?: number;
  organisationCount?: number;
}) => {
  const { t } = useTranslation();
  const organisationCards = organisations.map((organisation: any, index) => {
    return (
      <AccountOrganisationCard
        parent={orgsInGroups[organisation.id]}
        key={organisation.id}
        id={organisation.id}
        isPrivateOrg={organisation.visibility === 'PRIVATE'}
        orgCount={organisation?.organisations?.length}
        name={organisation.name}
        type={organisation?.type ? 'Group' : 'Organisation'}
        groupType={organisation?.type}
        sx={{
          paddingBottom: '1rem !important',
          paddingRight: index % 2 === 0 ? '1rem !important' : '0',
        }}
      />
    );
  });

  const inviteeHasAccessToShOnly: boolean = organisations.every(
    (org) => org.visibility === 'PRIVATE',
  );

  const orgId = organisations[0]?.organisations
    ? organisations[0]?.organisations[0]?.id
    : organisations[0]?.id;

  return (
    <StyledDiv
      sx={{
        '& > div:first-of-type': {
          marginTop: '40px',
        },
      }}
      data-testid="existing-user-details"
    >
      <SectionTitle
        icon={{ icon: <StyledOrganisationIcon />, iconColour: 'green' }}
        text={t(
          'ui.event-management.users.add-existing-practitioner.current-organisations.title',
        )}
      />
      <DetailsDescription
        role={user?.role ?? 'STANDARD'}
        id={user?.id ?? ''}
        groupCount={groupCount}
        organisationCount={organisationCount}
        organisationId={orgId ?? ''}
        inviteeHasAccessToShOnly={inviteeHasAccessToShOnly}
      />
      <Grid container sx={{ marginTop: '1.5rem' }}>
        {organisationCards}
      </Grid>
    </StyledDiv>
  );
};

export default ExistingUserDetails;
